import type { ListBuilderConfiguration } from '../types';
import { LICENSE_STATUS_STYLE_MAP } from './license/status-style-map';

/**
 * License Links embedded list configuration.
 *
 * Drives the Links tab on the license-detail page. Data source:
 * `GET /api/licenses/:id/links` — with any list params present, the
 * controller routes to `LicenseLinksService.findAll` (cross-DB UNION
 * subquery, filterable / sortable / paginated).
 *
 * The data shape is project-flat (no TypeORM entity for the underlying
 * `lm_individual_facility_links` cross-database table). `objectTable`
 * fields use `lm_individual_facility_links` as a scoping label only —
 * filters resolve through the service, not the shared TypeORM filter
 * builder. Filter keys come from
 * `libs/core/src/filters/config/license-links.ts`
 * (`linkSide`, `linkRole`, `linkSource`, `linkStatus`).
 *
 * Counterparty fields (name, license number, status, etc.) are flattened
 * onto each row by the service mapper, so the columns reference them
 * directly without going through a nested-object lookup.
 */
export const LICENSE_LINKS_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-license-links-embedded',
			cacheTTL: 300000,
			name: 'Links',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'counterparty-column',
					fields: [
						{
							id: 'counterparty-name',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'counterparty.name',
									objectTable: 'lm_individual_facility_links',
								},
								defaultValue: '(Unknown)',
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'counterparty-break',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
						},
						{
							id: 'counterparty-license-number',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'counterparty.licenseNumber',
									objectTable: 'lm_individual_facility_links',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-counterparty', text: 'Counterparty' }],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'counterpartyName',
							objectTable: 'lm_individual_facility_links',
						},
					],
					minWidth: 260,
				},
				{
					// F34 + F40: renamed "Side" → "Type" (clearer to the
					// rep). Humanize values via valueMapping displayText
					// (the engine's primary alias — displayValue was
					// silently dropped).
					id: 'type-column',
					fields: [
						{
							id: 'type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'counterparty.side',
									objectTable: 'lm_individual_facility_links',
								},
							},
							displayStyle: 'badge',
							styleConfig: {
								size: 'sm',
								valueMapping: [
									{ value: 'individual', displayText: 'Individual' },
									{ value: 'facility', displayText: 'Facility' },
								],
								colorRule: {
									type: 'status_map',
									statusMap: {
										individual: { type: 'info' },
										facility: { type: 'secondary' },
									},
								},
							},
						},
					],
					headers: [{ id: 'h-type', text: 'Type' }],
					sortable: false,
					width: 110,
				},
				{
					// F34 + F40: humanize role values via valueMapping
					// displayText. `pic` and `pharmacist_in_charge` both
					// map to "Pharmacist in Charge" — same role, two
					// scraper-side names.
					id: 'role-column',
					fields: [
						{
							id: 'role-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'role',
									objectTable: 'lm_individual_facility_links',
								},
							},
							displayStyle: 'badge',
							styleConfig: {
								size: 'sm',
								valueMapping: [
									{
										value: 'staff_pharmacist',
										displayText: 'Staff Pharmacist',
									},
									{ value: 'intern', displayText: 'Intern' },
									{ value: 'pic', displayText: 'Pharmacist in Charge' },
									{
										value: 'pharmacist_in_charge',
										displayText: 'Pharmacist in Charge',
									},
									{ value: 'technician', displayText: 'Technician' },
								],
								colorRule: {
									type: 'status_map',
									statusMap: {
										staff_pharmacist: { type: 'default' },
										intern: { type: 'default' },
										pic: { type: 'info' },
										pharmacist_in_charge: { type: 'info' },
										technician: { type: 'default' },
									},
								},
							},
						},
					],
					headers: [{ id: 'h-role', text: 'Role' }],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'role',
							objectTable: 'lm_individual_facility_links',
						},
					],
					width: 200,
				},
				// F34: Dropped 'confidence-column' and 'source-column'.
				// Both surfaced internal match-method metadata that's
				// not actionable to the rep — confidence scores live
				// elsewhere (the registry match-review surface) and the
				// link_source value (e.g. 'same_license_address') tells
				// the rep HOW the link was inferred, which doesn't
				// affect whether the link is valid.
				{
					id: 'status-column',
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'counterparty.status',
									objectTable: 'lm_individual_facility_links',
								},
							},
							displayStyle: 'badge',
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'status_map',
									statusMap: { ...LICENSE_STATUS_STYLE_MAP },
								},
							},
						},
					],
					headers: [{ id: 'h-status', text: 'Status' }],
					sortable: false,
					width: 110,
				},
				// F34: Dropped 'effective-column'. lm_individual_
				// facility_links.effective_date and end_date are 100%
				// NULL across all 126,442 rows (verified). Showing the
				// column with "-" / "-" on every row is pure noise.
				// If/when scrapers start populating these dates, the
				// column can be added back.
				{
					id: 'links-actions-column',
					fields: [
						{
							id: 'links-actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									// PD-14173: open the linked (counterparty)
									// license's type-aware registry detail (reuses the
									// PD-13999 modal), so a linked license — and its
									// captured file — is reachable from this list.
									id: 'view-linked-license',
									label: 'View License',
									iconName: 'faEye',
									callbackName: 'viewLinkedLicense',
									visibilityCallback: 'canViewLinkedLicense',
									variant: 'secondary',
									visibility: 'visible',
									showLabel: true,
								},
							],
						},
					],
					headers: [{ id: 'h-links-actions', text: '' }],
					sortable: false,
					width: 140,
				},
			],
			// F34: Dropped the confidence-desc + effective-desc sort
			// options since the underlying columns are gone. Name sort
			// is the only meaningful default now.
			defaultSortId: 'name-asc',
		},
	],
	// Shared table-level sort options live at the configuration root and use the
	// TableSortOption shape (`name` + `fields`) — the resolver reads
	// `configuration.tableSorts`, so a list-nested block or `label`/`sortFields`
	// keys silently fail to resolve `defaultSortId` (PD-14531 / PD-14533).
	tableSorts: [
		{
			id: 'name-asc',
			name: 'Name A → Z',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'counterpartyName',
					objectTable: 'lm_individual_facility_links',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: ['linkSide', 'linkRole', 'linkSource', 'linkStatus'],
	},
	objects: [
		{
			baseUrl: '/licenses/:id/links',
			objectLabel: 'License Link',
			objectTable: 'lm_individual_facility_links',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'role', fieldLabel: 'Role' },
				{ fieldName: 'linkSource', fieldLabel: 'Source' },
				{ fieldName: 'confidenceScore', fieldLabel: 'Confidence' },
				{ fieldName: 'effectiveDate', fieldLabel: 'Effective Date' },
				{ fieldName: 'endDate', fieldLabel: 'End Date' },
				{
					fieldName: 'tenantConfirmationCount',
					fieldLabel: 'Tenant Confirmations',
				},
				{ fieldName: 'counterparty.name', fieldLabel: 'Counterparty Name' },
				{
					fieldName: 'counterparty.licenseNumber',
					fieldLabel: 'License Number',
				},
				{ fieldName: 'counterparty.side', fieldLabel: 'Side' },
				{ fieldName: 'counterparty.status', fieldLabel: 'Status' },
				{ fieldName: 'counterparty.stateCode', fieldLabel: 'State' },
				{
					fieldName: 'counterparty.licenseType',
					fieldLabel: 'License Type',
				},
			],
		},
	],
};
